import {
  init,
  makeBrowserOfflineTransport,
  makeFetchTransport,
  reactRouterV5BrowserTracingIntegration,
  replayIntegration,
} from '@sentry/react';
import type { ErrorEvent, EventHint } from '@sentry/react';
import { assert, isBotUserAgent, isE2ESession, isFeatureEnabled, isProduction } from 'common/utils';
import { reduxHistory } from 'reduxHistory';

const REPLAY_WHITE_LIST_SEVERITY = ['error', 'warning', 'fatal'];

/**
 * CefSharp email-scanner errors. Email security scanners (e.g. Outlook Safe
 * Links) pre-check URLs with an embedded browser that has known bugs.
 * See: https://github.com/DataDog/browser-sdk/issues/2715
 */
const CEF_SHARP_ERROR_PATTERN = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

/**
 * Cross-browser phrasings for a fetch that never received an HTTP response:
 * Chrome "Failed to fetch", Firefox "NetworkError when attempting to fetch
 * resource", Safari "Load failed".
 */
const NETWORK_ERROR_PATTERN = /NetworkError when attempting to fetch resource|Failed to fetch|Load failed/;

/**
 * Datadog browser SDK intake host. A network error naming this host means the
 * SDK could not ship its telemetry batch.
 */
const DATADOG_INTAKE_PATTERN = /browser-intake-datadoghq\.com/;

/**
 * Tag applied to downgraded Datadog telemetry-intake failures so they remain
 * queryable (e.g. for a threshold alert) without paging on individual events.
 */
const TELEMETRY_INTAKE_FAILURE_CATEGORY = 'telemetry-intake-failure';

/**
 * Collects the candidate error messages from a Sentry event and its hint so
 * they can be matched against known noise patterns.
 *
 * @param event - The Sentry error event about to be sent.
 * @param hint - Sentry event hint that may carry the original exception.
 * @returns The non-empty error message strings found on the event and hint.
 */
const getErrorMessages = (event: ErrorEvent, hint?: EventHint): string[] => {
  const messages: string[] = [];

  const exceptionValue = event.exception?.values?.[0]?.value;
  if (exceptionValue) {
    messages.push(exceptionValue);
  }

  const error = hint?.originalException;
  if (typeof error === 'string') {
    messages.push(error);
  } else if (error instanceof Error) {
    messages.push(error.message);
  } else if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') {
      messages.push(message);
    }
  }

  return messages;
};

/**
 * Sentry `beforeSend` hook. Filters out third-party noise and downgrades
 * Datadog telemetry-intake network failures.
 *
 * CefSharp email-scanner errors are dropped entirely as they are never
 * actionable. Datadog intake network failures are downgraded to `info` and
 * tagged rather than dropped: per-event they are noise (a user's ad-blocker,
 * proxy or dropped connection), but a spike signals something worth knowing —
 * a Datadog outage, a CSP regression blocking the intake host, or a growing
 * observability blind-spot — so they stay queryable behind a threshold alert.
 *
 * @param event - The Sentry error event about to be sent.
 * @param hint - Sentry event hint containing the original exception.
 * @returns The (possibly mutated) event, or null to drop it.
 */
export const beforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  const messages = getErrorMessages(event, hint);

  if (messages.some((message) => CEF_SHARP_ERROR_PATTERN.test(message))) {
    return null;
  }

  // Require both the network-error phrase and the Datadog intake host in the
  // same message so genuine "Failed to fetch" errors against our own API are
  // not swallowed.
  const isDatadogIntakeFailure = messages.some(
    (message) => NETWORK_ERROR_PATTERN.test(message) && DATADOG_INTAKE_PATTERN.test(message)
  );
  if (isDatadogIntakeFailure) {
    event.level = 'info';
    event.tags = { ...event.tags, category: TELEMETRY_INTAKE_FAILURE_CATEGORY };
  }

  return event;
};

export const initSentry = () => {
  if (isE2ESession() || isBotUserAgent()) {
    return;
  }

  assert(window._env_.VITE_APP_API_BASE_URL);
  init({
    dsn: window._env_.VITE_APP_SENTRY_DSN,
    integrations: [
      reactRouterV5BrowserTracingIntegration({ history: reduxHistory }),
      replayIntegration({
        maskAllText: isProduction(),
        maskAllInputs: isProduction(),
        blockAllMedia: isProduction(),
        /**
         * https://docs.sentry.io/platforms/javascript/session-replay/understanding-sessions/#ignore-certain-errors-for-error-sampling
         * don't record replays for simple logs
         */
        beforeErrorSampling: (event) => (event.level ? REPLAY_WHITE_LIST_SEVERITY.includes(event.level) : true),
      }),
    ],
    release: window._env_.VITE_APP_TAG,
    environment: window._env_.VITE_APP_ENVIRONMENT,
    enabled: isFeatureEnabled('SentryEnabled'),
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 9,
    transport: makeBrowserOfflineTransport(makeFetchTransport),
    beforeSend,
  });
};
